import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CourseContent from '../components/CourseContent';
import MCQList from '../components/MCQList';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';
import courseService from '../services/courseService';
import { openWhatsApp } from '../utils/whatsappHelper';

const CourseDetail = () => {
  const { id } = useParams();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already enrolled in this course
  const isEnrolled = isAuthenticated && user?.enrolled_courses?.some(
    enrolledCourse => enrolledCourse.id === parseInt(id)
  );

  // Fetch course details and scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCourseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * Fetch course details from backend
   */
  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await courseService.getCourseById(id);
      setCourse(data);
    } catch (err) {
      console.error('Failed to fetch course details:', err);
      if (err.response?.status === 404) {
        setError('Course not found');
      } else {
        setError(err.response?.data?.detail || 'Failed to load course details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <LoadingSpinner message="Loading course details..." />
        <Footer />
      </div>
    );
  }

  // Error or Not Found state
  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="container mx-auto max-w-7xl px-4 py-16">
          <ErrorMessage
            message={error || 'Course not found'}
            onRetry={error !== 'Course not found' ? fetchCourseDetails : null}
          />
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/courses')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Courses
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleEnroll = () => {
    // If already enrolled, do nothing
    if (isEnrolled) {
      return;
    }

    // If user is not authenticated, redirect to login page
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // If authenticated, open WhatsApp with course details
    const message = `Hi, I'm interested in enrolling for the promotion material`;
    openWhatsApp('919219485048', message);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      {/* Course Header */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-8 sm:py-12 md:py-16">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white hover:text-blue-100 transition-colors mb-4 sm:mb-6"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm sm:text-base font-medium">Back to Courses</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight">
                {course.title}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-4 sm:mb-6 leading-relaxed">
                {course.description}
              </p>

              {/* Course Meta */}
              <div className="flex items-center gap-2 text-blue-100 mb-4 sm:mb-6">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-sm sm:text-base md:text-lg">Created by Learners Edge</span>
              </div>

              {/* Price and Enroll */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <div>
                  <span className="text-white font-bold text-2xl sm:text-3xl md:text-4xl">
                    {course.price === 0 ? 'Free' : `₹${course.price}`}
                  </span>
                </div>
                <button
                  onClick={handleEnroll}
                  disabled={isEnrolled}
                  className={`w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg transition-colors font-semibold text-base sm:text-lg shadow-lg ${
                    isEnrolled
                      ? 'bg-green-100 text-green-700 cursor-not-allowed border-2 border-green-300'
                      : 'bg-white text-blue-600 hover:bg-gray-100 hover:shadow-xl'
                  }`}
                >
                  {isEnrolled ? 'Enrolled' : isAuthenticated ? 'Enroll Now' : 'Login to Enroll'}
                </button>
              </div>
            </div>

            {/* Course Image */}
            <div className="bg-sky-100 rounded-lg overflow-hidden shadow-xl order-1 lg:order-2">
              {course.image ? (
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-contain"
                />
              ) : (
                <div className="w-full h-48 sm:h-64 md:h-80 lg:h-96 bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center p-4">
                  <p className="text-white text-xl sm:text-2xl font-semibold text-center">{course.title}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Course Content */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* What You'll Learn */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">What You'll Learn</h2>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5 sm:mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm sm:text-base text-slate-700">Comprehensive coverage of all exam topics and patterns</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5 sm:mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm sm:text-base text-slate-700">Complete study materials and practice questions</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5 sm:mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm sm:text-base text-slate-700">Mock tests and performance analysis</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5 sm:mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm sm:text-base text-slate-700">Doubt clearing sessions and Q&A support</span>
                  </li>
                </ul>
              </div>

              {/* Course Content */}
              {course.modules && course.modules.length > 0 && (
                <CourseContent content={course.modules} userHasAccess={course.user_has_access || false} />
              )}

              {/* MCQ Tests */}
              <MCQList courseId={id} />

              {/* Course Description */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">Course Description</h2>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-4">
                  This comprehensive banking promotion course is designed to help you excel in your career advancement.
                  Our experienced faculty provides in-depth coverage of all topics required for the promotion exam.
                </p>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                  With study materials and practice tests, you'll be fully prepared to achieve your promotion goals
                  with our proven methodology.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Course Features */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:sticky lg:top-4">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">This course includes:</h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 sm:gap-3 text-slate-700">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm sm:text-base">Downloadable resources</span>
                  </li>
                  <li className="flex items-center gap-2 sm:gap-3 text-slate-700">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm sm:text-base">Lifetime access</span>
                  </li>
                </ul>

                <button
                  onClick={handleEnroll}
                  disabled={isEnrolled}
                  className={`w-full mt-4 sm:mt-6 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-colors font-semibold text-sm sm:text-base ${
                    isEnrolled
                      ? 'bg-green-100 text-green-700 cursor-not-allowed border-2 border-green-300'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isEnrolled ? 'Enrolled' : isAuthenticated ? 'Enroll Now' : 'Login to Enroll'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CourseDetail;
